'use client';

import { ArrowRight, Loader2, Upload } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { type FormEvent, useCallback, useState } from 'react';

import { contactFieldClass } from '@/components/landing/contact/constants';
import { env } from '@/configs/env';
import { Link, useRouter } from '@/i18n/routing';

import { apiErrorCode } from './errors';
import { Turnstile } from './Turnstile';

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const FILE_EXTENSIONS = ['pdf', 'docx', 'png', 'jpg', 'jpeg', 'webp'];
const ACCEPT = FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',');
const LINKEDIN_PATTERN =
  'https://([a-zA-Z]{2,3}\\.)?linkedin\\.com/in/[^/?#\\s]+/?';

const labelClass = 'font-matter text-text-primary text-[14px] font-medium';

function fileError(file: File | null): string | null {
  if (!file) return 'resume_required';
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!FILE_EXTENSIONS.includes(extension)) return 'file_type_not_allowed';
  if (file.size === 0) return 'file_empty';
  if (file.size > MAX_FILE_BYTES) return 'file_too_large';
  return null;
}

export function ApplyForm({ jobId }: { jobId: string }) {
  const t = useTranslations('Jobs');
  const locale = useLocale();
  const router = useRouter();
  const [token, setToken] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onToken = useCallback((value: string) => setToken(value), []);
  const siteKey = env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const ready = Boolean(siteKey && env.NEXT_PUBLIC_API_URL);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const form = new FormData(event.currentTarget);
    const resume = form.get('resume');
    const problem = fileError(
      resume instanceof File && resume.name ? resume : null
    );
    if (problem) {
      setError(problem);
      return;
    }
    if (!token) {
      setError('captcha_failed');
      return;
    }

    form.delete('consent_box');
    form.set('consent', 'true');
    form.set('locale', locale);
    form.set('turnstile_token', token);

    setSubmitting(true);
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/public/jobs/${encodeURIComponent(jobId)}/apply`,
        { method: 'POST', body: form }
      );
      if (response.ok) {
        router.push(`/jobs/${jobId}/applied`);
        return;
      }
      setError((await apiErrorCode(response)) ?? 'generic');
    } catch {
      setError('generic');
    }
    // the token was used, so the widget must give a new one
    setToken('');
    setResetKey((key) => key + 1);
    setSubmitting(false);
  }

  if (!ready) {
    return (
      <p
        role="alert"
        className="font-matter bg-surface-error text-status-error rounded-[12px] px-4 py-3 text-[14px]"
      >
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-[20px]">
      <div className="grid gap-[14px] sm:grid-cols-2">
        <div className="flex flex-col gap-[8px]">
          <label htmlFor="full_name" className={labelClass}>
            {t('fieldName')}
          </label>
          <input
            id="full_name"
            name="full_name"
            required
            maxLength={255}
            autoComplete="name"
            className={contactFieldClass}
          />
        </div>

        <div className="flex flex-col gap-[8px]">
          <label htmlFor="email" className={labelClass}>
            {t('fieldEmail')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            maxLength={320}
            autoComplete="email"
            dir="ltr"
            className={contactFieldClass}
          />
        </div>

        <div className="flex flex-col gap-[8px]">
          <label htmlFor="phone" className={labelClass}>
            {t('fieldPhone')}
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            maxLength={50}
            autoComplete="tel"
            dir="ltr"
            className={contactFieldClass}
          />
        </div>

        <div className="flex flex-col gap-[8px]">
          <label htmlFor="linkedin_url" className={labelClass}>
            {t('fieldLinkedin')}
          </label>
          <input
            id="linkedin_url"
            name="linkedin_url"
            type="url"
            maxLength={500}
            pattern={LINKEDIN_PATTERN}
            placeholder="https://www.linkedin.com/in/"
            dir="ltr"
            className={contactFieldClass}
          />
        </div>
      </div>

      <label
        htmlFor="resume"
        className="border-text-muted bg-surface-card flex cursor-pointer flex-col items-center gap-2 rounded-[18px] border border-dashed px-4 py-8 text-center"
      >
        <Upload size={26} className="text-brand-accent-cyan" aria-hidden />
        <span className={labelClass}>{t('fieldResume')}</span>
        <span className="font-matter text-text-secondary text-[14px]">
          {t('fieldResumeHint')}
        </span>
        <input
          id="resume"
          name="resume"
          type="file"
          required
          accept={ACCEPT}
          className="font-matter text-text-secondary max-w-full text-[14px]"
        />
      </label>

      <div className="flex flex-col gap-[8px]">
        <label htmlFor="cover_note" className={labelClass}>
          {t('fieldNote')}
        </label>
        <textarea
          id="cover_note"
          name="cover_note"
          rows={4}
          maxLength={2000}
          className={`${contactFieldClass} resize-y`}
        />
      </div>

      <label className="font-matter text-text-secondary flex items-start gap-3 text-[14px] leading-[22px]">
        <input
          type="checkbox"
          name="consent_box"
          required
          className="mt-[3px]"
        />
        <span>
          {t('consent')}{' '}
          <Link href="/privacy#job-applicants" className="underline">
            {t('consentLink')}
          </Link>
        </span>
      </label>

      {siteKey && (
        <Turnstile
          siteKey={siteKey}
          locale={locale}
          resetKey={resetKey}
          onToken={onToken}
        />
      )}

      {error && (
        <p
          role="alert"
          className="font-matter bg-surface-error text-status-error rounded-[12px] px-4 py-3 text-[14px]"
        >
          {t.has(`errors.${error}`)
            ? t(`errors.${error}`)
            : t('errors.generic')}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="brand-gradient-button font-matter flex h-[56px] w-full cursor-pointer items-center justify-center gap-2 rounded-[16px] text-[17px] font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-70"
      >
        {submitting ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            {t('submitting')}
          </>
        ) : (
          <>
            {t('submit')}
            <ArrowRight size={18} className="rtl:rotate-180" />
          </>
        )}
      </button>
    </form>
  );
}
